import { Cause, Effect, Fiber, Layer, Ref, Schema, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { makeSessionOwner } from "../session-owner.ts";
import { shimmerSwapMs, type ShimmerSnapshot } from "./shimmer-model.ts";
import { TextShimmers, type ShimmerSession } from "./shimmer-service.ts";

const closed = (scope: Scope.Scope) => scope.state._tag === "Closed";
export const TextShimmersLive = (root: Scope.Scope) => Layer.effect(TextShimmers, Effect.gen(function* () {
  const registry = yield* AtomRegistry.AtomRegistry;
  const failures = yield* Ref.make<readonly Cause.Cause<never>[]>([]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(values => values.length
    ? Effect.failCause(Cause.fromReasons(values.flatMap(value => value.reasons))) : Effect.void)));
  const sessions = yield* makeSessionOwner(root);
  const makeSession = (initial: boolean) => Effect.gen(function* () {
    const owner = yield* Scope.Scope;
    const active = Schema.is(Schema.Boolean)(initial) ? initial : false;
    const state = yield* Ref.make<{ readonly sequence: number; readonly snapshot: ShimmerSnapshot; readonly timer: Fiber.Fiber<void> | null }>({ sequence: 0, snapshot: { active, run: active }, timer: null });
    const read = Ref.get(state).pipe(Effect.map(value => value.snapshot)), snapshot = Atom.make(read);
    const alive = () => !closed(root) && !closed(owner);
    const publish = Effect.sync(() => { if (alive()) registry.refresh(snapshot); });
    const receive = (next: boolean) => Effect.uninterruptible(Effect.gen(function* () {
      if (!alive() || !Schema.is(Schema.Boolean)(next)) return;
      const claimed = yield* Ref.modify(state, current => current.snapshot.active === next
        ? [null, current] as const
        : [{ sequence: current.sequence + 1, timer: current.timer }, { sequence: current.sequence + 1, snapshot: { active: next, run: next || current.snapshot.run }, timer: null }] as const);
      if (claimed === null) return;
      if (claimed.timer) yield* Fiber.interrupt(claimed.timer);
      const sequence = claimed.sequence;
      if (!alive() || (yield* Ref.get(state)).sequence !== sequence) return;
      yield* publish;
      if (next) return;
      const reset = Effect.sleep(shimmerSwapMs).pipe(Effect.andThen(Ref.update(state, current => alive() && current.sequence === sequence
        ? { ...current, snapshot: { active: false, run: false }, timer: null } : current)), Effect.andThen(publish),
        Effect.catchCause(cause => cause.reasons.some(Cause.isDieReason) ? Ref.update(failures, values => [...values, cause]) : Effect.void));
      const timer = yield* Effect.forkIn(reset, owner, { uninterruptible: false });
      const retained = yield* Ref.modify(state, current => alive() && current.sequence === sequence && current.snapshot.run
        ? [true, { ...current, timer }] as const : [false, current] as const);
      if (!retained) yield* Fiber.interrupt(timer);
    }));
    return { snapshot, read, receive } satisfies ShimmerSession;
  });
  return TextShimmers.of({ open: active => sessions.open(makeSession(active)) });
}));
